import { createSelector } from 'reselect'

import { getOnlyDate } from '~/astra'
import { getFlag } from '~/hooks/useFlag'
import { StoreState, useStore } from '~/store/store'
import { getTheaterRoom } from '~/utils/dips/surgeryResources'

import {
    Include,
    ScheduledSurgery,
    selectUnScheduledSurgeries,
    StormQueryOfPlural,
    UnScheduledSurgery,
    useScheduledSurgeries,
    useScheduledSurgery,
    useUnScheduledSurgeries,
    useUnScheduledSurgery,
} from '..'
import { filterBySelectedSpecialties } from './filterBySelectedSpecialties'

const selectSchedulingSurgeries = (state: StoreState) => state.operationalPlanner.schedulingSurgeries

export { selectSchedulingSurgeries }

export type SchedulingParams = StoreState['operationalPlanner']['schedulingSurgeries'][number]

/** Stable booking-id list for Storm `in` / `notIn` — avoids new cache keys every render. */
export const selectSchedulingBookingIds = createSelector(selectSchedulingSurgeries, surgeries => surgeries.map(s => s.bookingId))

export const selectSchedulingParamsByBookingId = createSelector(
    [selectSchedulingSurgeries, (_state: StoreState, bookingId: string) => bookingId],
    (surgeries, bookingId): SchedulingParams | undefined => {
        if (bookingId === '') return undefined
        return surgeries.find(params => params.bookingId === bookingId)
    }
)

export function useSchedulingBookingIds() {
    return useStore(selectSchedulingBookingIds)
}

export function useSchedulingParamsByBookingId(bookingId: string) {
    return useStore(state => selectSchedulingParamsByBookingId(state, bookingId))
}

export function useIsBookingBeingScheduled(bookingId: string): boolean {
    return useSchedulingParamsByBookingId(bookingId) !== undefined
}

/** Combined surgery type covering both scheduled and unscheduled storm surgeries. */
export type Surgery = ScheduledSurgery | UnScheduledSurgery
export type SchedulingSurgery = UnScheduledSurgery & {
    sagaId: string
    plannedTheatreInTime: string

    /** `YYYY-MM-DD` of `plannedTheatreInTime` */
    scheduled_date: string
    /** `shortName` of the filled theater-room slot in the scheduling params */
    room_code?: string | undefined
}

/** Common includes for surgeries: folder metadata and section */
export const COMMON_SURGERY_INCLUDE = {
    surgeryFolderMetadata: true,
    section: true,
} as const

export type CommonSurgery = Include<Surgery, typeof COMMON_SURGERY_INCLUDE>
export type CommonUnScheduledSurgery = Include<UnScheduledSurgery, typeof COMMON_SURGERY_INCLUDE>
export type CommonScheduledSurgery = Include<ScheduledSurgery, typeof COMMON_SURGERY_INCLUDE>

/** Hook to fetch unscheduled surgeries automatically populated with common metadata and relation includes */
export function useCommonUnScheduledSurgeries(query?: StormQueryOfPlural<typeof useUnScheduledSurgeries>) {
    return useUnScheduledSurgeries({
        ...(query ?? {}),
        include: { ...COMMON_SURGERY_INCLUDE, ...query?.include },
    })
}

/** Hook to fetch scheduled surgeries automatically populated with common metadata and relation includes */
export function useCommonScheduledSurgeries(query?: StormQueryOfPlural<typeof useScheduledSurgeries>) {
    return useScheduledSurgeries({
        ...(query ?? {}),
        include: { ...COMMON_SURGERY_INCLUDE, ...query?.include },
    })
}

/** Singular common unscheduled surgery lookup by booking id. */
export function useCommonUnScheduledSurgery(bookingId: string) {
    return useUnScheduledSurgery({ where: { bookingId }, include: COMMON_SURGERY_INCLUDE })
}

/** Relations read by patient/available-time suggestion scoring and filtering. */
export const PATIENT_SUGGESTION_SURGERY_INCLUDE = {
    ...COMMON_SURGERY_INCLUDE,
    surgeryResources: true,
    surgeryType: true,
    surgeryMetadataSuggestions: true,
} as const

export type PatientSuggestionSurgery = Include<UnScheduledSurgery, typeof PATIENT_SUGGESTION_SURGERY_INCLUDE>
export type ScheduledPatientSuggestionSurgery = Include<ScheduledSurgery, typeof PATIENT_SUGGESTION_SURGERY_INCLUDE>
export type PatientSuggestionCandidate = PatientSuggestionSurgery | ScheduledPatientSuggestionSurgery

/** Waiting list currently needs the same populated shape as patient suggestions. */
export const WAITING_LIST_SURGERY_INCLUDE = PATIENT_SUGGESTION_SURGERY_INCLUDE

/** Scheduled waiting-list rows need `location` for unscheduling toast navigation — see `hasUnscheduleLocationIncluded`. */
export const WAITING_LIST_SCHEDULED_SURGERY_INCLUDE = {
    ...WAITING_LIST_SURGERY_INCLUDE,
    location: true,
} as const

export type WaitingListSurgery = PatientSuggestionSurgery
export type WaitingListScheduledSurgery = Include<ScheduledSurgery, typeof WAITING_LIST_SCHEDULED_SURGERY_INCLUDE>
export type WaitingListSchedulingSurgery = WaitingListSurgery & Pick<SchedulingSurgery, 'sagaId' | 'plannedTheatreInTime' | 'scheduled_date' | 'room_code'>

/** Hydrates one in-flight scheduling row from operational-planner params + an unscheduled surgery slice row. */
export function buildSchedulingSurgeryFromParams<T extends UnScheduledSurgery>(unscheduled: T, schedulingParams: SchedulingParams): T & SchedulingSurgery {
    return {
        ...unscheduled,
        contact: { ...unscheduled.contact, checkInTime: schedulingParams.checkInTime, tentative: 'NotTentative' as const },
        surgeryResources: schedulingParams.resources,
        sagaId: schedulingParams.sagaId,
        plannedTheatreInTime: schedulingParams.plannedTheatreInTime,
        scheduled_date: getOnlyDate(schedulingParams.plannedTheatreInTime),
        room_code: getTheaterRoom(schedulingParams.resources)?.shortName ?? undefined,
    }
}

export function useWaitingListUnScheduledSurgery(bookingId: string): WaitingListSurgery | undefined {
    return useUnScheduledSurgery({ where: { bookingId }, include: WAITING_LIST_SURGERY_INCLUDE })
}

export function useWaitingListScheduledSurgery(bookingId: string): WaitingListScheduledSurgery | undefined {
    return useScheduledSurgery({ where: { id: bookingId }, include: WAITING_LIST_SCHEDULED_SURGERY_INCLUDE })
}

/** Waiting-list resolver matching legacy `selectSelectedSurgery` precedence. */
export function useWaitingListSurgeryByBookingId(
    bookingId: string
): WaitingListSurgery | WaitingListScheduledSurgery | WaitingListSchedulingSurgery | undefined {
    const isBeingScheduled = useIsBookingBeingScheduled(bookingId)
    const unscheduled = useWaitingListUnScheduledSurgery(bookingId)
    const scheduled = useWaitingListScheduledSurgery(bookingId)
    const schedulingParams = useSchedulingParamsByBookingId(bookingId)

    if (!isBeingScheduled && unscheduled) return unscheduled
    if (scheduled) return scheduled
    if (isBeingScheduled && unscheduled && schedulingParams) return buildSchedulingSurgeryFromParams(unscheduled, schedulingParams)

    return undefined
}

function mergeOptimisticUnScheduledWhere(schedulingBookingIds: readonly string[], callerWhere?: StormQueryOfPlural<typeof useUnScheduledSurgeries>['where']) {
    const defaultWhere = schedulingBookingIds.length > 0 ? { bookingId: { notIn: schedulingBookingIds } } : undefined
    return callerWhere && defaultWhere ? { AND: [callerWhere, defaultWhere] } : (callerWhere ?? defaultWhere)
}

/** Unscheduled surgeries excluding those currently being scheduled — for event handlers and thunks. */
export function selectOptimisticUnScheduledSurgeriesForTesting(state: StoreState, query?: StormQueryOfPlural<typeof useUnScheduledSurgeries>) {
    const schedulingBookingIds = selectSchedulingBookingIds(state)
    const mergedWhere = mergeOptimisticUnScheduledWhere(schedulingBookingIds, query?.where)

    return selectUnScheduledSurgeries(state, {
        ...(query ?? {}),
        ...(mergedWhere ? { where: mergedWhere } : {}),
    })
}

/** Unscheduled waiting-list surgeries, including those currently being scheduled in the operational planner, across every department. */
export function selectOptimisticUnScheduledSurgeries(state: StoreState): WaitingListSurgery[] {
    return selectUnScheduledSurgeries(state, {
        where: mergeOptimisticUnScheduledWhere(selectSchedulingBookingIds(state), undefined),
        include: {
            ...WAITING_LIST_SURGERY_INCLUDE,
            section: { department: true },
        },
    })
}

/**
 * Unscheduled waiting-list surgeries, including those currently being scheduled in the operational
 * planner, for the user's currently selected department filter.
 * Returns everything, unfiltered, when that filter is 'all'.
 */
export const selectOptimisticUnScheduledSurgeriesBySelectedDepartmentOrSelectedSpecialties = createSelector(
    selectOptimisticUnScheduledSurgeries,
    (state: StoreState) => state.appFilters.departmentKey,
    (state: StoreState) => state.appFilters.selectedSpecialties,
    (surgeries, departmentKey, selectedSpecialties): WaitingListSurgery[] => {
        if (getFlag('use_specialties_selection')) {
            return [...filterBySelectedSpecialties(surgeries, selectedSpecialties)]
        }

        if (departmentKey === 'all') {
            return surgeries
        }

        return surgeries.filter(surgery => surgery.section?.department_id === departmentKey)
    }
)

/** Unscheduled surgeries including those currently being scheduled in the operational planner, across every department. */
export function useOptimisticUnScheduledSurgeries(): WaitingListSurgery[] {
    return useStore(selectOptimisticUnScheduledSurgeries)
}

/** Unscheduled surgeries including those currently being scheduled in the operational planner, for the user's currently selected department filter. */
export function useOptimisticUnScheduledSurgeriesBySelectedDepartmentOrSelectedSpecialties(): WaitingListSurgery[] {
    return useStore(selectOptimisticUnScheduledSurgeriesBySelectedDepartmentOrSelectedSpecialties)
}
